<template>
  <div class="aftersales section-content-group-block" :class="customClass">
    <div v-for="button in afterSalesButtonsRef" :key="button.name">
      <button
        role="button"
        class="
          iconcta iconcta--multi-layout
          icon--hoverable
          hover-shadow
          iconcta-modifier--no-wrap
        "
        @click="openModal(button.name)"
      >
        <span class="sr-only">{{ button.ariaLabel }}</span>
        <div class="iconcta__holder">
          <i :class="button.icon" aria-hidden="true" />
        </div>
        <div class="iconcta__content bg-KQ-White">
          <p class="text-KQ-Grey1">{{ button.title }}</p>
        </div>
        <div class="iconcta__chevron">
          <i
            class="icon icon-chevronright icon-color--red icon--xxs"
            aria-hidden="true"
          />
        </div>
      </button>
    </div>
  </div>
  <base-modal
    modal-class="base-model--side base-model--overflow aftersales-modal"
    :additional-styles="{ top: modalTopOffsetValue }"
    @update:state="isOpen = false"
  >
    <template #modal-content>
      <div class="base-model__content">
        <after-sales-wrapper
          v-if="afterSalesContentType === 'Check in'"
          :title="checkInTitle"
          :show-links="showLinksInModal"
          :after-sales-links="afterSalesLinks"
          :content-type="afterSalesContentType"
          class="check-in__notifications"
          @close:modal="isOpen = false"
          @openModal="openModal($event)"
        >
          <check-in :button-text="checkInButtonText">
            <ul>
              <li
                v-for="(child, index) in slotChildren"
                :key="child?.props?.alertID"
                aria-hidden="true"
                class="check-in__notifications"
                @mouseover="mouseOverHandler"
                @mouseleave="mouseLeaveHandler"
                @focus="focusHandler"
                @blur="blurHandler"
              >
                <notification-banner
                  v-bind="{ ...child?.props }"
                  :class="{ 'check-in__notification': index === 0 }"
                  @elBlur="blurHandler"
                  @elFocus="focusHandler"
                  @isDismissed="removeEl"
                ></notification-banner>
              </li>
            </ul>
          </check-in>
        </after-sales-wrapper>

        <after-sales-wrapper
          v-if="afterSalesContentType === 'Manage booking'"
          :show-links="showLinksInModal"
          :title="manageBookingTitle"
          :after-sales-links="afterSalesLinks"
          :content-type="afterSalesContentType"
          @close:modal="isOpen = false"
          @openModal="openModal($event)"
        >
          <manage-booking
            :button-text="manageBookingButtonText"
          ></manage-booking>
        </after-sales-wrapper>

        <after-sales-wrapper
          v-if="afterSalesContentType === 'Flight status'"
          :show-links="showLinksInModal"
          :title="flightStatusTitle"
          :after-sales-links="afterSalesLinks"
          :content-type="afterSalesContentType"
          @close:modal="isOpen = false"
          @openModal="openModal($event)"
        >
          <flight-status :button-text="flightStatusButtonText"></flight-status>
        </after-sales-wrapper>

        <after-sales-wrapper
          v-if="afterSalesContentType === 'Flight schedule'"
          :show-links="showLinksInModal"
          :title="flightScheduleTitle"
          :after-sales-links="afterSalesLinks"
          :content-type="afterSalesContentType"
          @close:modal="isOpen = false"
          @openModal="openModal($event)"
        >
          <flight-schedule
            :button-text="flightScheduleButtonText"
          ></flight-schedule>
        </after-sales-wrapper>
      </div>
    </template>
  </base-modal>
</template>

<script setup>
import { ref, onMounted, useSlots, provide } from 'vue';
import AfterSalesWrapper from './AfterSalesWrapper.vue';
import BaseModal from '../BaseModal/BaseModal.vue';
import isJSONString from '../../hooks/isJsonString';
import CheckIn from '../CheckIn/CheckIn.vue';
import useFocusTrap from '../../hooks/useFocusTrap';
import NotificationBanner from '../NotificationBanner/NotificationBanner.vue';
import ManageBooking from '../ManageBooking/ManageBooking.vue';
import FlightStatus from '../FlightStatus/FlightStatus.vue';
import FlightSchedule from '../FlightSchedule/FlightSchedule.vue';
import { useHeaderHeight } from '../../hooks/useHeaderHeight';

const slots = useSlots();
const slotChildren = ref(slots.default ? Array.from(slots.default()) : []);

const { trapRef, initFocusTrap } = useFocusTrap();

const props = defineProps({
  afterSalesButtons: {
    type: undefined,
    default: () => [],
  },
  afterSalesLinks: {
    type: undefined,
    default: () => [],
  },
  checkInTitle: {
    type: String,
    default: '',
  },
  checkInButtonText: {
    type: String,
    default: '',
  },
  manageBookingTitle: {
    type: String,
    default: '',
  },
  manageBookingButtonText: {
    type: String,
    default: '',
  },
  flightStatusTitle: {
    type: String,
    default: '',
  },
  flightStatusButtonText: {
    type: String,
    default: '',
  },
  flightScheduleTitle: {
    type: String,
    default: '',
  },
  flightScheduleButtonText: {
    type: String,
    default: '',
  },
  showLinksInModal: {
    type: Boolean,
    default: false,
  },
  hasGreyBg: {
    type: Boolean,
    default: true,
  },
  customClass: {
    type: String,
    default: 'mobile-2-col desktop-4-col',
  },
});

const isOpen = ref(false);
provide('injectedIsOpen', isOpen);

const afterSalesContentType = ref('');
const afterSalesButtonsRef = ref([]);
const modalTopOffsetValue = ref(0);
const { useHeaderH, useNotificationsH } = useHeaderHeight();
const headerHeight = useHeaderH();
const notificationsHeight = useNotificationsH();

const getModalOffsetHandler = () => {
  const scroll = document.body.scrollTop || document.documentElement.scrollTop;;
  return scroll >= notificationsHeight.value
    ? headerHeight.value + 'px'
    : headerHeight.value + notificationsHeight.value - scroll + 'px';
};

const openModal = (value) => {
  isOpen.value = true;
  afterSalesContentType.value = value;
  modalTopOffsetValue.value = getModalOffsetHandler();
  setTimeout(() => {
    initFocusTrap();
  }, 300);
};

onMounted(() => {
  if (typeof props.afterSalesButtons === 'object') {
    afterSalesButtonsRef.value = props.afterSalesButtons;
  } else if (typeof props.afterSalesButtons === 'string') {
    if (isJSONString(props.afterSalesButtons)) {
      afterSalesButtonsRef.value = JSON.parse(props.afterSalesButtons);
    }
  }
});
</script>
